import { getUiLocale, uiText } from '../i18n/uiText';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { PolicyDialog } from '../components/legal/PolicyReader';
import { policyCopy } from '../legal/policy';
import { useAuth } from '../store/AuthContext';
import { authApi, getApiErrorMessage } from '../api';

// Учётную запись представителя создают администратор, аудитор или уполномоченный
// сотрудник его организации. Поэтому на странице входа доступны:
//  • вход
//  • восстановление доступа (пароля)

export default function LoginPage() {
  React.useEffect(() => {
    document.documentElement.classList.add('login-route-active');
    document.body.classList.add('login-route-active');
    return () => {
      document.documentElement.classList.remove('login-route-active');
      document.body.classList.remove('login-route-active');
    };
  }, []);
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(loginForm.username, loginForm.password);
      navigate('/');
    } catch (err) {
      setError(getApiErrorMessage(err) || uiText("Неверный логин или пароль"));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await authApi.requestPasswordReset({ email: forgotEmail });
      setRequestSent(true);
    } catch (err) {
      setError(getApiErrorMessage(err) || uiText("Не удалось запросить сброс пароля"));
    } finally {
      setLoading(false);
    }
  };

  const returnToLogin = () => {
    setForgotMode(false);
    setRequestSent(false);
    setForgotEmail('');
    setError('');
  };

  return (
    <div className="login-shell minimal-login-shell">
      <div className="login-card login-card-compact minimal-login-card">
        <div className="login-brand-block">
          <img
            src="/audit-pro-app-icon-v1.png"
            alt="Audit Pro"
            className="minimal-login-logo minimal-login-app-icon"
          />
          <h1>{uiText("Вход в систему")}</h1>
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
        {!forgotMode ? (
          <form onSubmit={handleLogin} autoComplete="on">
            <div className="form-group">
              <label className="form-label required">{uiText("Логин")}</label>
              <input className="form-control" type="text" name="username" autoComplete="username" value={loginForm.username} onChange={e => setLoginForm(prev => ({ ...prev, username: e.target.value }))} required autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">{uiText("Пароль")}</label>
              <input className="form-control" type="password" name="password" autoComplete="current-password" value={loginForm.password} onChange={e => setLoginForm(prev => ({ ...prev, password: e.target.value }))} />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading} style={{ width: '100%', justifyContent: 'center', padding: '12px' }}>
              {loading ? uiText("Вход...") : uiText("Войти")}
            </button>
            <button type="button" className="btn btn-ghost minimal-forgot-link" onClick={() => { setForgotMode(true); setError(''); }}>{uiText("Забыл пароль")}</button>
            <a className="btn btn-ghost minimal-mobile-link" href="/audit-pro/mobile/">
              <span aria-hidden="true">▣</span>{uiText("Мобильное проведение аудита")}</a>
          </form>
        ) : requestSent ? (
          <div className="login-stack password-request-confirmation" role="status">
            <div className="password-request-confirmation-mark" aria-hidden="true">✓</div>
            <h2>{uiText("Заявка отправлена")}</h2>
            <p>{uiText("Администраторы получили уведомление. После одобрения логин и временный пароль придут на ")}<b>{forgotEmail}</b>.</p>
            <p className="form-hint">{uiText("Старый пароль продолжает действовать, пока администратор не одобрит сброс.")}</p>
            <button type="button" className="btn btn-primary" onClick={returnToLogin}>{uiText("Вернуться ко входу")}</button>
          </div>
        ) : (
          <div className="login-stack">
            <form onSubmit={handleForgotRequest} className="password-reset-request-form" autoComplete="off">
              <div className="password-reset-intro">
                <h2>{uiText("Восстановление доступа")}</h2>
                <p>{uiText("Укажите email учетной записи. Администратор проверит заявку и при одобрении система отправит временный пароль.")}</p>
              </div>
              <div className="form-group">
                <label className="form-label required">{uiText("Email учетной записи")}</label>
                <input className="form-control" type="email" name="password_reset_email" autoComplete="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} required />
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? uiText("Отправка...") : uiText("Отправить заявку")}
              </button>
              <button type="button" className="btn btn-ghost" onClick={returnToLogin}>{uiText("Назад ко входу")}</button>
            </form>
          </div>
        )}
        <div className="login-legal-footer">
          <a className="login-legal-link" href={`${process.env.PUBLIC_URL || '/audit-pro'}/privacy-policy`} onClick={event => {
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            // Safari does not always focus links on pointer activation. Give the
            // dialog a reliable return target without touching the form state.
            event.currentTarget.focus({ preventScroll: true });
            setPolicyOpen(true);
          }}><ShieldCheck size={16} aria-hidden="true" /><span>{policyCopy[getUiLocale()].link}</span></a>
        </div>
      </div>
      {policyOpen && <PolicyDialog onClose={() => setPolicyOpen(false)} />}
    </div>
  );
}
